/**
 * Typed env accessor for the render bundle.
 *
 * Vite inlines `import.meta.env.*` at build time. This module is the only
 * place that reads from it. Everything else imports the frozen `env` object.
 *
 * Validation runs at module load with zod. If the schema fails, we throw an
 * `EnvValidationError` carrying every offending field. The boot UI catches
 * this and renders a fallback view instead of letting the app crash.
 *
 * No secrets live here — only the public-facing tenant API URL and an
 * optional dev-only mock URL for previewing brands without a live engine.
 */

import { z } from 'zod';
import { reportError } from './observability.js';

const EnvSchema = z.object({
  MODE: z.enum(['development', 'production', 'test']).default('production'),
  /**
   * URL of the tenant-api host. Production is reverse-proxied behind the
   * render origin so the value can be a path-only string in browser.
   */
  VITE_TENANT_API_URL: z
    .string()
    .min(1, 'VITE_TENANT_API_URL is required')
    .default('/tenant-api'),
  /**
   * Optional. Dev-only fixture URL used by `TenantConfigProvider` instead
   * of the live tenant-api when set. Lets the team preview brand bundles
   * locally without a CMS or engine running.
   */
  VITE_MOCK_TENANT_CONFIG_URL: z.string().min(1).optional(),
  /**
   * Origin allowed by the strict `connect-src` portion of the CSP. The
   * Vite plugin substitutes this into `index.html` at build time.
   */
  VITE_TENANT_API_ORIGIN: z
    .string()
    .min(1)
    .default("'self' http://api.localhost https://api.atlas.insideformation.com"),
  /**
   * Origins allowed by the `font-src` portion of the CSP. Default is the
   * minimum-viable set (`'self' data:`) — tenants that pull fonts from a
   * specific CDN must add it explicitly via this env var. The previous
   * value was the wildcard `'self' https: data:`, which permitted any
   * HTTPS origin to ship fonts and exposed the browser to font-based
   * fingerprinting / abuse vectors.
   */
  VITE_FONT_ORIGINS: z.string().min(1).default("'self' data:"),
});

export type Env = Readonly<z.infer<typeof EnvSchema>>;

export class EnvValidationError extends Error {
  public override readonly name = 'EnvValidationError';
  public readonly issues: ReadonlyArray<{ path: string; message: string }>;

  constructor(issues: ReadonlyArray<{ path: string; message: string }>) {
    super(
      `Render env validation failed:\n${issues
        .map((issue) => `  - ${issue.path}: ${issue.message}`)
        .join('\n')}`,
    );
    this.issues = issues;
  }
}

function loadEnv(): Env {
  // Vite inlines these reads. We pass through `import.meta.env` so missing
  // keys come through as `undefined` and zod's defaults take over.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const raw = import.meta.env;
  const parsed = EnvSchema.safeParse(raw);

  if (!parsed.success) {
    const issues = parsed.error.issues.map((issue) => ({
      path: issue.path.join('.') || '(root)',
      message: issue.message,
    }));
    const err = new EnvValidationError(issues);
    // Surface via observability — boot UI displays a friendly version.
    // Only field names are echoed, never values, so secrets can't leak
    // into a structured log line.
    reportError(err, { scope: 'env-loader', issues });
    throw err;
  }

  return Object.freeze(parsed.data);
}

export const env: Env = loadEnv();
